import { fabric } from 'fabric';
import {
  AttachmentPoint,
  type ConnectControlWithNormalDefinition,
} from '../types';
import { type Attachable } from '../objects/Connector';
import { isShape } from '../objects/guards';
import paper from 'paper';
import type { IPoint, Point } from 'fabric/fabric-impl';
import { lineLength, pointInRect } from './geometry';

// How far a connector is from the object it connects to.
export const STANDOFF_DISTANCE = 6;

interface AttachablePoint {
  attachmentPoint: AttachmentPoint;
  position: fabric.Point;
}

/**
 * Validates and returns a Paper.js path for edge attachment calculations.
 * If the shape doesn't declare an edge path, defaults to a rectangular
 * bounding box.
 * Returns undefined if path is invalid.
 */
const validateEdgePaperPath = (
  obj: Attachable,
  scaled = true
): paper.Path | undefined => {
  if (!isShape(obj)) {
    const path = new paper.Path.Rectangle(
      new paper.Point(-obj.width / 2, -obj.height / 2),
      new paper.Point(obj.width / 2, obj.height / 2)
    );

    if (scaled) {
      const transformMatrix = obj.calcTransformMatrix();
      path.transform(new paper.Matrix(...transformMatrix));
    }

    return path;
  }

  const paperPath = obj.shape.getEdgeAttachmentPath(scaled);
  if (!paperPath) {
    return;
  }

  if (!(paperPath instanceof paper.Path)) {
    console.error(
      'Invalid paper path for edge attachment',
      paperPath,
      obj.type,
      obj.shapeName
    );
    return;
  }

  const pathLength = paperPath.length;
  if (!paperPath || pathLength <= 0) {
    console.debug('Invalid paper path for edge attachment');
    return;
  }

  return paperPath;
};

/** All the information needed to render a connector attachment and associated UI. */
interface EdgeAttachment {
  /** absolute point on canvas on the edge where we are snapping to, for accurate distance calculations */
  snapPoint: fabric.Point;
  /** absolute point on canvas where the connector will end (considers strokeWidth and standoff) */
  point: fabric.Point;
  /** absolute point on canvas where the blue dot renders (considers strokeWidth) */
  indicator: fabric.Point;
  /** offset (0-1) around the edge path used to persist the attachment point */
  offset: number;
  /** vector direction that the connector should follow */
  normal: IPoint;
}

/**
 * Complete edge attachment calculation from edge offset.
 * Returns the final attachment point with standoff applied.
 * Uses unscaled path to ensure attachment points remain stable under non-uniform scaling.
 */
export const calculateEdgeAttachmentFromOffset = (
  obj: Attachable,
  edgeOffset: number
): EdgeAttachment | undefined => {
  const unscaledPath = validateEdgePaperPath(obj, false);
  if (!unscaledPath) return;

  edgeOffset = (edgeOffset + 1) % 1; // normalise to 0-1

  const pathLength = unscaledPath.length;
  const offsetPosition = edgeOffset * pathLength;
  const pointOnLine = unscaledPath.getPointAt(offsetPosition);
  const tangent = unscaledPath.getTangentAt(offsetPosition);

  if (tangent.x === 0 && tangent.y === 0) {
    // In practice, this only happens when the path is invalid, which generally
    // should not happen. However, if it does, handle it here to avoid crashing.
    console.error(
      'Invalid tangent value found, cannot compute edge attachment'
    );
    return;
  }

  // Transform point from unscaled path coordinates to absolute canvas coordinates
  const localPoint = new fabric.Point(pointOnLine.x, pointOnLine.y);
  const transformMatrix = obj.calcTransformMatrix();
  const snapPoint = fabric.util.transformPoint(localPoint, transformMatrix);

  // Transform the tangent vector (as a direction, not a point)
  // Transformation matrix is [a, b, c, d, e, f] where:
  // a, b, c, d are rotation/scale components
  // e, f are translation components (which we ignore for vectors)
  const [a, b, c, d] = transformMatrix;

  const transformedTangent = new fabric.Point(
    a * tangent.x + c * tangent.y,
    b * tangent.x + d * tangent.y
  );

  // Calculate normal from the transformed tangent (perpendicular, rotated 90 degrees)
  const transformedNormal = new fabric.Point(
    transformedTangent.y,
    -transformedTangent.x
  );

  // Normalize to unit length
  const normalLength = Math.sqrt(
    transformedNormal.x * transformedNormal.x +
      transformedNormal.y * transformedNormal.y
  );
  const normalizedTransformedNormal = new fabric.Point(
    transformedNormal.x / normalLength,
    transformedNormal.y / normalLength
  );

  // Calculate the final attachment point position including standoff distance and stroke width
  const strokeWidth =
    obj.stroke && obj.stroke !== 'transparent' ? obj.strokeWidth : 0;
  const totalStandoff = STANDOFF_DISTANCE + strokeWidth / 2;

  const point = new fabric.Point(
    snapPoint.x + normalizedTransformedNormal.x * totalStandoff,
    snapPoint.y + normalizedTransformedNormal.y * totalStandoff
  );
  const indicator = new fabric.Point(
    snapPoint.x + (normalizedTransformedNormal.x * strokeWidth) / 2,
    snapPoint.y + (normalizedTransformedNormal.y * strokeWidth) / 2
  );

  // Return normal in local space (scaled but not rotated) for connector rendering
  // Scale the tangent first, then calculate normal from it
  const scaledTangent = new fabric.Point(
    tangent.x * obj.scaleX,
    tangent.y * obj.scaleY
  );
  // Normal is perpendicular to the scaled tangent
  const scaledNormal = new fabric.Point(scaledTangent.y, -scaledTangent.x);
  const scaledNormalLength = Math.sqrt(
    scaledNormal.x * scaledNormal.x + scaledNormal.y * scaledNormal.y
  );
  const normal = {
    x: scaledNormal.x / scaledNormalLength,
    y: scaledNormal.y / scaledNormalLength,
  };

  return { snapPoint, point, indicator, offset: edgeOffset, normal };
};

/** Do approximate check to see if shape's bounding box is within threshold
 * of a position. This will give false positives but will eliminate most
 * shapes without needing expensive path calculations
 */
export const maybeWithinThreshold = (
  obj: fabric.Object,
  position: fabric.Point,
  threshold: number
) => {
  const bbox = obj.getBoundingRect(true);
  return pointInRect(position, {
    left: bbox.left - threshold,
    top: bbox.top - threshold,
    width: bbox.width + threshold * 2,
    height: bbox.height + threshold * 2,
  });
};

/**
 * Calculates the distance from a point to the nearest point on a shape's edge.
 * Returns undefined if the shape is invalid or doesn't support edge attachment, or the distance exceeds the threshold.
 * Returns the path offset and attachment point adjusted for standoff and stroke width along with the distance.
 * Transforms absolute position to local coordinates, finds offset on unscaled path.
 */
export const calculateEdgeAttachmentFromPosition = (
  obj: Attachable,
  position: Point,
  threshold?: number
):
  | (EdgeAttachment & {
      distance: number;
    })
  | undefined => {
  const unscaledPath = validateEdgePaperPath(obj, false);
  const scaledPath = validateEdgePaperPath(obj, true);
  if (!unscaledPath || !scaledPath) return;

  // Find nearest point on the SCALED path in world coordinates
  // This ensures we find the visually nearest point
  const pointOnScaledLine = scaledPath.getNearestPoint(
    new paper.Point(position.x, position.y)
  );

  // Transform this point back to local coordinates
  const transformMatrix = obj.calcTransformMatrix();
  const invertedMatrix = fabric.util.invertTransform(transformMatrix);
  const localPointOnLine = fabric.util.transformPoint(
    new fabric.Point(pointOnScaledLine.x, pointOnScaledLine.y),
    invertedMatrix
  );

  // Find the nearest point on the unscaled path to this local point
  // This gives us the offset along the unscaled path
  const unscaledPointOnLine = unscaledPath.getNearestPoint(
    new paper.Point(localPointOnLine.x, localPointOnLine.y)
  );

  const unscaledPathOffset = unscaledPath.getOffsetOf(unscaledPointOnLine);
  const offset = unscaledPathOffset / unscaledPath.length;

  // Use calculateEdgeAttachmentFromOffset to get the final attachment with proper transformations
  const attachment = calculateEdgeAttachmentFromOffset(obj, offset);
  if (!attachment) return;

  // Calculate actual distance between original position and attachment point
  const distance = lineLength(position, attachment.snapPoint);

  if (threshold !== undefined && distance > threshold) {
    return;
  }

  return { ...attachment, distance };
};

/**
 * Checks if a point is actually contained within a shape's real path,
 * not just its bounding box.
 */
export const pointWithinObject = (
  obj: Attachable,
  point: fabric.Point
): boolean => {
  // First check bounding box, this is fast but gives false positives
  const vptPointer = fabric.util.transformPoint(
    point,
    obj.canvas.viewportTransform!
  );

  if (!obj.containsPoint(vptPointer)) {
    return false;
  }

  // For shapes, check against actual path using Paper.js.
  // Fallback to bounding box for non-shapes, or shapes without a valid path.
  if (!isShape(obj)) {
    return true;
  }

  const scaledPath = validateEdgePaperPath(obj, true);
  if (!scaledPath) {
    return true;
  }

  const paperPoint = new paper.Point(point.x, point.y);
  return scaledPath.contains(paperPoint);
};

/** Finds closest of the provided attachable points that is within a distance threshold */
export const closestAttachablePoint = (
  attachments: AttachablePoint[],
  point: IPoint,
  threshold: number
) => {
  let closest = Infinity;
  let closestAttachment: AttachablePoint | undefined;
  for (const attachment of attachments) {
    const dist = lineLength(attachment.position, point);
    if (dist < closest && dist < threshold) {
      closest = dist;
      closestAttachment = attachment;
    }
  }
  return closestAttachment;
};

/**
 * Calculates the canvas coordinate position for an attachment point on a connectable object.
 */
export const calculateAttachmentPosition = (
  obj: Attachable,
  attachmentPoint: AttachmentPoint,
  { withStandoff = true }
) => {
  // Handle fixed attachment points
  const controlDefinitions =
    obj.connectControlDefinition() as ConnectControlWithNormalDefinition;
  const offsets = controlDefinitions[attachmentPoint];

  // Maybe the shape changed.
  if (!offsets) return null;

  const strokeWidth = obj.stroke ? obj.strokeWidth : 0;
  const standoff = withStandoff ? STANDOFF_DISTANCE : 0;

  return fabric.util.transformPoint(
    new fabric.Point(
      offsets.x * (obj.width + strokeWidth / obj.scaleX) +
        (offsets.normal.x * standoff) / obj.scaleX,
      offsets.y * (obj.height + strokeWidth / obj.scaleY) +
        (offsets.normal.y * standoff) / obj.scaleY
    ),
    obj.calcTransformMatrix()
  );
};

/**
 * Return all of the points where a connector can attach to the object the cursor
 * is over.
 */
export const fetchAttachmentPoints = (
  overObject: Attachable | undefined | null,
  { withStandoff }: { withStandoff: boolean }
): AttachablePoint[] => {
  if (!overObject) {
    return [];
  }

  return overObject
    .attachmentPoints?.()
    .filter(
      // Edge and Center attachments are handled separately
      point =>
        point !== AttachmentPoint.Edge && point !== AttachmentPoint.Center
    )
    .map(attachmentPoint => {
      return {
        attachmentPoint,
        position: calculateAttachmentPosition(overObject, attachmentPoint, {
          withStandoff,
        }),
      };
    })
    .filter(attachment => !!attachment.position) as AttachablePoint[];
};

/** Returns true if connectors can be attached to the center of the object */
export const allowsCenterAttachment = (obj: Attachable): boolean =>
  obj.attachmentPoints?.().includes(AttachmentPoint.Center);
